/**
 * Course notification document schemas and Firestore paths.
 *
 * Notifications are stored at users/{userId}/notifications/{notificationId}.
 * Mirrors what is in Firestore; use client/admin modules for refs and converters.
 */

import {z} from 'zod';

import {FirestoreTimestampSchema} from '../types';
import type {UserId} from '../../types';

// ============================================================================
// Constants
// ============================================================================

export const NOTIFICATION_TYPE = {
  FORUM: 'forum',
  STUDENT_SECTION_REFLECTION: 'studentSectionReflection',
  /** Section leader post-section debrief survey (same document shape as student reflection). */
  SL_SECTION_DEBRIEF: 'slSectionDebrief',
  TEACH_NOW_STUDENT: 'teachNowStudent',
  /** A peer reviewer nominated the recipient's teaching moment (Teaching Stars). */
  SHADOW_MOMENT_NOMINATED: 'shadowMomentNominated',
  /** The other party posted an (approved) reply on a shared shadow feedback. */
  SHADOW_REPLY: 'shadowReply',
  /** Approved peer feedback about the recipient's own section is now available. */
  SHADOW_FEEDBACK_RECEIVED: 'shadowFeedbackReceived',
  /** A shadow review the recipient claimed is due (or overdue). */
  SHADOW_REVIEW_DUE: 'shadowReviewDue',
} as const;

export type NotificationType =
  (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE];

// ============================================================================
// Metadata schemas
// ============================================================================

export const ForumNotificationMetaDataSchema = z.object({
  forumId: z.string(),
  postId: z.string(),
  replyId: z.string(),
  /**
   * Uid of the replier. Omitted when the replier is the author of an
   * anonymous post, so the notification never names them.
   */
  from: z.string().optional(),
});

export type ForumNotificationMetaData = z.infer<
  typeof ForumNotificationMetaDataSchema
>;

export const PostSectionSurveyNotificationMetaDataSchema = z.object({
  sectionId: z.string(),
  sectionWeek: z.number(),
});

export type PostSectionSurveyNotificationMetaData = z.infer<
  typeof PostSectionSurveyNotificationMetaDataSchema
>;

export const ShadowMomentNominatedNotificationMetaDataSchema = z.object({
  /** Section the nominated moment(s) came from. */
  sectionId: z.string(),
  /** 1-based section week. */
  sectionWeek: z.number(),
  /** How many of the recipient's moments the reviewer nominated in one feedback. */
  count: z.number(),
  /** Reviewer's display name captured at nomination time, when available. */
  reviewerName: z.string().optional(),
});

export type ShadowMomentNominatedNotificationMetaData = z.infer<
  typeof ShadowMomentNominatedNotificationMetaDataSchema
>;

export const ShadowReplyNotificationMetaDataSchema = z.object({
  /** Reviewer of the parent feedback (its document id). */
  reviewerUid: z.string(),
  /** Section of the parent feedback. */
  sectionId: z.string(),
  /** 1-based section week. */
  sectionWeek: z.number(),
  /** Assignment id (`{reviewerUid}__{sectionId}__{sectionWeek}`), for deep-linking. */
  assignmentId: z.string(),
  /** Reply author's display name, when available. */
  authorName: z.string().optional(),
});

export type ShadowReplyNotificationMetaData = z.infer<
  typeof ShadowReplyNotificationMetaDataSchema
>;

export const ShadowFeedbackReceivedNotificationMetaDataSchema = z.object({
  /** Reviewer who wrote the feedback (the feedback doc id). */
  reviewerUid: z.string(),
  /** The recipient's section the feedback is about. */
  sectionId: z.string(),
  /** 1-based section week. */
  sectionWeek: z.number(),
  /** Assignment id (`{reviewerUid}__{sectionId}__{sectionWeek}`), for deep-linking. */
  assignmentId: z.string(),
  /** Reviewer's display name captured at approval time, when available. */
  reviewerName: z.string().optional(),
});

export type ShadowFeedbackReceivedNotificationMetaData = z.infer<
  typeof ShadowFeedbackReceivedNotificationMetaDataSchema
>;

export const ShadowReviewDueNotificationMetaDataSchema = z.object({
  /** Section the claimed review is for. */
  sectionId: z.string(),
  /** 1-based section week. */
  sectionWeek: z.number(),
  /** Assignment id (`{reviewerUid}__{sectionId}__{sectionWeek}`), for deep-linking. */
  assignmentId: z.string(),
  /** Due date as epoch ms, when the assignment carries one. */
  dueAtMs: z.number().optional(),
});

export type ShadowReviewDueNotificationMetaData = z.infer<
  typeof ShadowReviewDueNotificationMetaDataSchema
>;

// ============================================================================
// Document schema (discriminated union by type)
// ============================================================================

const BaseNotificationSchema = z.object({
  timestamp: FirestoreTimestampSchema,
  type: z.enum([
    NOTIFICATION_TYPE.FORUM,
    NOTIFICATION_TYPE.STUDENT_SECTION_REFLECTION,
    NOTIFICATION_TYPE.SL_SECTION_DEBRIEF,
    NOTIFICATION_TYPE.TEACH_NOW_STUDENT,
    NOTIFICATION_TYPE.SHADOW_MOMENT_NOMINATED,
    NOTIFICATION_TYPE.SHADOW_REPLY,
    NOTIFICATION_TYPE.SHADOW_FEEDBACK_RECEIVED,
    NOTIFICATION_TYPE.SHADOW_REVIEW_DUE,
  ]),
  acknowledged: z.boolean(),
  courseId: z.string(),
});

export const ForumCourseNotificationSchema = BaseNotificationSchema.extend({
  type: z.literal(NOTIFICATION_TYPE.FORUM),
  metaData: ForumNotificationMetaDataSchema,
}).loose();

/** Student section reflection and SL section debrief share the same shape. */
export const PostSectionSurveyCourseNotificationSchema =
  BaseNotificationSchema.extend({
    type: z.enum([
      NOTIFICATION_TYPE.STUDENT_SECTION_REFLECTION,
      NOTIFICATION_TYPE.SL_SECTION_DEBRIEF,
    ]),
    metaData: PostSectionSurveyNotificationMetaDataSchema,
    /**
     * When a student reflection notification stops showing. SL debrief
     * notifications are written without one; older ones carry it, and the
     * client ignores it for them.
     */
    expiresAt: FirestoreTimestampSchema.optional(),
  }).loose();

export const TeachNowStudentCourseNotificationSchema =
  BaseNotificationSchema.extend({
    type: z.literal(NOTIFICATION_TYPE.TEACH_NOW_STUDENT),
    metaData: z.record(z.string(), z.unknown()).optional().default({}),
  }).loose();

/** A Teaching Star nomination of the recipient's teaching moment(s). */
export const ShadowMomentNominatedCourseNotificationSchema =
  BaseNotificationSchema.extend({
    type: z.literal(NOTIFICATION_TYPE.SHADOW_MOMENT_NOMINATED),
    metaData: ShadowMomentNominatedNotificationMetaDataSchema,
  }).loose();

/** A reply on a shadow feedback the recipient is a party to. */
export const ShadowReplyCourseNotificationSchema =
  BaseNotificationSchema.extend({
    type: z.literal(NOTIFICATION_TYPE.SHADOW_REPLY),
    metaData: ShadowReplyNotificationMetaDataSchema,
  }).loose();

/** Approved peer feedback about the recipient's own section. */
export const ShadowFeedbackReceivedCourseNotificationSchema =
  BaseNotificationSchema.extend({
    type: z.literal(NOTIFICATION_TYPE.SHADOW_FEEDBACK_RECEIVED),
    metaData: ShadowFeedbackReceivedNotificationMetaDataSchema,
  }).loose();

/** A claimed shadow review that is due (or overdue). */
export const ShadowReviewDueCourseNotificationSchema =
  BaseNotificationSchema.extend({
    type: z.literal(NOTIFICATION_TYPE.SHADOW_REVIEW_DUE),
    metaData: ShadowReviewDueNotificationMetaDataSchema,
  }).loose();

/** Union (not discriminated): survey branch matches two `type` values in one schema. */
export const CourseNotificationDocumentSchema = z.discriminatedUnion('type', [
  ForumCourseNotificationSchema,
  PostSectionSurveyCourseNotificationSchema,
  TeachNowStudentCourseNotificationSchema,
  ShadowMomentNominatedCourseNotificationSchema,
  ShadowReplyCourseNotificationSchema,
  ShadowFeedbackReceivedCourseNotificationSchema,
  ShadowReviewDueCourseNotificationSchema,
]);

export type CourseNotificationDocument = z.infer<
  typeof CourseNotificationDocumentSchema
>;

export type ForumCourseNotification = z.infer<
  typeof ForumCourseNotificationSchema
>;
export type PostSectionSurveyCourseNotification = z.infer<
  typeof PostSectionSurveyCourseNotificationSchema
>;
export type SlSectionDebriefCourseNotification = Extract<
  PostSectionSurveyCourseNotification,
  {type: typeof NOTIFICATION_TYPE.SL_SECTION_DEBRIEF}
>;
export type TeachNowStudentCourseNotification = z.infer<
  typeof TeachNowStudentCourseNotificationSchema
>;
export type ShadowMomentNominatedCourseNotification = z.infer<
  typeof ShadowMomentNominatedCourseNotificationSchema
>;
export type ShadowReplyCourseNotification = z.infer<
  typeof ShadowReplyCourseNotificationSchema
>;
export type ShadowFeedbackReceivedCourseNotification = z.infer<
  typeof ShadowFeedbackReceivedCourseNotificationSchema
>;
export type ShadowReviewDueCourseNotification = z.infer<
  typeof ShadowReviewDueCourseNotificationSchema
>;

// ============================================================================
// Paths
// ============================================================================

export const NOTIFICATIONS_COLLECTION = 'notifications' as const;

export function getCourseNotificationCollectionPath(userId: UserId): string {
  return `users/${userId}/${NOTIFICATIONS_COLLECTION}`;
}

export function getCourseNotificationDocumentPath({
  userId,
  notificationId,
}: {
  userId: UserId;
  notificationId: string;
}): string {
  return `${getCourseNotificationCollectionPath(userId)}/${notificationId}`;
}
